<template>
  <div class="pt-2">
    <div class="bg-white dark:bg-gray-800 relative shadow-sm sm:rounded-lg border border-gray-200">
      <div class="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 p-4">

        <!-- Search Input -->
        <div class="w-full md:w-1/3">
          <label for="telephony-accounts-search" class="sr-only">Search</label>
          <div class="relative w-full">
            <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg aria-hidden="true" class="w-5 h-5 text-gray-500 dark:text-gray-400" fill="currentColor"
                viewbox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clip-rule="evenodd" />
              </svg>
            </div>
            <input type="text" id="telephony-accounts-search"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
              placeholder="Search" required=""
              v-model="search">
          </div>
        </div>

        <!-- New Account Button -->
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0">
          <a
            href="/telephony_accounts/new"
            class="px-5 py-2 text-sm font-medium text-white inline-flex items-center bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">

            <svg class="w-5 h-5 me-2 text-gray-200 dark:text-white" aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M5 12h14m-7 7V5" />
            </svg>
            Account
          </a>
        </div>
      </div>

      <!-- eslint-disable vue/no-unused-vars, vue/no-template-shadow -->
      <vue-slim-tables
        ref="table"
        :source="tableSource"
        :columns="columns"
        class="table"
        :per-page="pageSize">
        <template #thead="{ columns, orders }">
          <tr>
            <th>
              {{ columns[0].title }}
            </th>

            <th>
              {{ columns[1].title }}
            </th>

            <th>
              {{ columns[2].title }}
            </th>

            <th>
              {{ columns[3].title }}
            </th>

            <th>
              <div class="flex items-center justify-end">
                {{ columns[4].title }}
              </div>
            </th>

            <th>
              {{ columns[5].title }}
            </th>
          </tr>
        </template>

        <template #row="{ row, index, column, value }">
          <tr>
            <td class="font-medium text-gray-900 whitespace-nowrap w-48">
              <div class="truncate w-48">
                {{ row.friendly_name }}
              </div>
            </td>

            <td class="truncate w-48">
              {{ capitalize(row.provider) }}
            </td>

            <td class="truncate w-48">
              {{ row.account_sid }}
            </td>

            <td>
              {{ row.sync_finished_at }}
            </td>

            <td>
              <div class="flex items-center justify-end">
                <span v-if="row.syncing"
                  class="inline-block text-left bg-orange-100 text-orange-800 text-xs font-medium my-0 pl-1 pr-1.5 py-0.5 rounded dark:bg-orange-900 dark:text-orange-300 h-6">

                  <svg
                    class="inline w-4 h-4 mr-1 text-orange-400 animate-spin"
                    viewBox="0 0 100 101"
                    fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="#E5E7EB" />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentColor" />
                  </svg>

                  Syncing

                </span>

                <span v-else-if="row.status === 'active'"
                  class="inline-block text-left bg-green-100 text-green-800 text-xs font-medium my-0 pl-1 pr-1.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                  <svg class="w-4 h-4 text-green-400 inline" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                    width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M5 11.917 9.724 16.5 19 7.5" />
                  </svg>

                  {{ humanize(row.status) }}
                </span>

                <span v-else
                  class="w-34 inline-block text-left bg-red-100 text-red-800 text-xs font-medium ml-1 pl-1 pr-1.5 py-0.5 rounded dark:bg-red-900 dark:text-red-300">
                  <svg class="w-4 h-4 text-red-400 inline" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                    fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M7.757 12h8.486M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                  {{ humanize(row.status || '') }}
                </span>
              </div>
            </td>

            <td class="truncate w-2">
              <div>
                <context-menu>
                  <ul class="text-sm text-gray-700 dark:text-gray-200">
                    <li class="p-1">
                      <button
                        :disabled="row.syncing"
                        @click="syncAccount(row)"
                        :class="[
                          'block py-2 px-4 w-full text-left hover:bg-gray-100',
                          row.syncing ? 'cursor-not-allowed' : ''
                        ]">

                        <span v-if="row.syncing" class="text-gray-400">
                          Syncing...
                        </span>
                        <span v-else>
                          Sync
                        </span>
                      </button>
                    </li>

                    <li class="p-1">
                      <a
                        :href="`/telephony_accounts/${row.id}/edit`"
                        class="block py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white">

                        Edit
                      </a>
                    </li>
                  </ul>
                </context-menu>
              </div>
            </td>
          </tr>
        </template>

        <template #pagination>
          <nav class="flex flex-col md:flex-row justify-between items-start md:items-center space-y-3 md:space-y-0">
            <span class="text-sm font-normal text-gray-500 dark:text-gray-400">
              Showing
              <span class="font-semibold text-gray-900 dark:text-white">{{ currentPageInfo() }}</span>
              of
              <span class="font-semibold text-gray-900 dark:text-white">{{ totalCount }}</span>
            </span>

            <VuejsPaginate
              v-if="totalPagesCount > 1"
              v-model="currentPage"
              :page-count="totalPagesCount"
              :prev-text="'<'"
              :next-text="'>'" />
          </nav>
        </template>
      </vue-slim-tables>
      <!-- eslint-enable vue/no-unused-vars, vue/no-template-shadow -->
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import VueSlimTables from 'vue-slim-tables'
import securedAxios from '../../utils/axios'
import qs from 'qs'
import usePagination from '~/javascript/components/composables/pagination'
import VuejsPaginate from 'vuejs-paginate-next'
import { toastError, toastSuccess } from '~/javascript/utils/toast'
import { capitalize, humanize } from '~/javascript/utils/strings'
import { createConsumer } from '@rails/actioncable'
import ContextMenu from '../common/context_menu.vue'

const axios = securedAxios()

const table = ref()
const search = ref('')

let searchTriggerTimeout = null

const consumer = createConsumer()
let subscription = null

const {
  totalPagesCount,
  totalCount,
  currentPage,
  pageSize,
  setPagination,
  currentPageInfo
} = usePagination({ pageSize: 10 })

const columns = [
  { title: 'Friendly Name', key: 'friendly_name' },
  { title: 'Provider', key: 'providers' },
  { title: 'SID', key: 'account_sid' },
  { title: 'Last synced at', key: 'sync_finished_at' },
  { title: 'Status', key: 'status' },
  { title: '', key: 'actions' }
]

async function tableSource(params) {
  let response = null

  try {
    response = await axios.get(
      '/ajax/telephony_accounts',
      {
        params: { ...params, query: search.value, page: currentPage.value },
        paramsSerializer(json) {
          return qs.stringify(json, { arrayFormat: 'brackets' })
        }
      }
    )

    setPagination(response)
  } catch (error) {
    toastError(error)
  }

  return response?.data || []
}

async function syncAccount(row) {
  try {
    row.syncing = true
    await axios.post(`/ajax/telephony_accounts/${row.id}/sync`)
  } catch (error) {
    toastError(error)
  }
}

watch(search, (_val) => {
  clearTimeout(searchTriggerTimeout)
  searchTriggerTimeout = setTimeout(() => {
    // Reset the number of page to 1 when searching otherwise the table will crash
    currentPage.value = 1
    table?.value?.reload()
  }, 500)
})

watch(currentPage, (_val) => {
  table?.value?.reload()
})

onMounted(() => {
  if (subscription) {
    subscription.unsubscribe()
    subscription = null
  }

  subscription = consumer.subscriptions.create(
    { channel: 'TelephonyAccountChannel' },
    {
      received(data) {
        if (data.success) {
          toastSuccess(data.message)
        } else {
          toastError(data.message)
        }
        table?.value?.reload()
      }
    }
  )
})

onBeforeUnmount(() => {
  if (subscription) {
    subscription.unsubscribe()
  }
})
</script>
